<template>
    <div class="datepicker-list gap-4 p-0 pl-4 pr-4 bg-white">
        <div v-if="Object.keys(dates).length">
            <div class="flex justify-between w-full mb-4">
                <span
                    class="list--header flex h-10 w-full text-center center flex-row justify-center self-stretch text-text-default font-Open font-bold"
                    v-if="bookingStore.state?.selected_date">
                    {{ formatDate(bookingStore.state?.selected_date, false) }}
                    <span
                        class="ml-2"
                        v-if="bookingStore.state?.start_time">
                        - {{ convertTo12Hour(bookingStore.state.start_time) }}
                    </span>
                </span>
            </div>
            <div class="coursel-wrapper">
                <carousel
                    :items-to-show="slidesByScreenValue"
                    :wrap-around="false"
                    ref="dateCarousel"
                    class="px-12">
                    <slide
                        v-for="(item, key, index) in dates"
                        :key="index">
                        <button
                            class="cursor-pointer"
                            @click="selectDate(key, index)">
                            <div
                                :class="[
                                    {
                                        'selected rounded border border-solid border-[#5856d6] bg-[#f7f7fd] shadow-sm':
                                            currentlySelectedDate === key,
                                    },
                                    'card  mb-2 p-8 flex flex-col justify-center items-center hover:border self-stretch py-2 px-4 pt-3 rounded border-solid',
                                ]">
                                <p class="font-Open">
                                    <span class="block w-full text-center">{{
                                        dayFromDate(key, false, true)
                                    }}</span>
                                    <span
                                        :class="[
                                            'block w-full text-center',
                                            {
                                                'font-bold':
                                                    currentlySelectedDate ===
                                                    key,
                                            },
                                        ]"
                                        >{{
                                            dayFromDate(key, false, false)
                                        }}</span
                                    >
                                </p>
                            </div>
                        </button>
                    </slide>
                    <template #addons>
                        <navigation />
                    </template>
                </carousel>
            </div>
            <div v-if="dynamicTimes.length">
                <h5 class="my-4 mx-auto text-[#717171] md:text-sm text-xs">
                    Choose a time that suits you
                </h5>
                <ul
                    class="flex-wrap date-time-slots grid pb-4 md:gap-6 gap-4 grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                    <li
                        v-for="(item, key, index) in dynamicTimes"
                        :key="index"
                        :class="['cursor-pointer w-42 item']"
                        @click="selectTime(item)">
                        <div
                            :class="[
                                {
                                    ['selected border border-solid border-[#5856d6] text-[#5856d6] font-bold bg-[#f7f7fd] shadow-sm']:
                                        currentlySelectedTime === item.start,
                                },
                                'card text-sm p-8 flex flex-col items-center justify-center self-stretch py-2 px-4 rounded-full border border-solid font-bold',
                            ]">
                            <div class="text-m">
                                {{ item.start_display }}
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <div v-else>
            <h1 class="text-center font-bold size-xxl">No dates available</h1>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted } from 'vue';
    import { useBookingStore } from '@/stores/booking';
    import {
        dayFromDate,
        formatDate,
        convertTo12Hour,
    } from '@/helpers/dateFormats';
    import { useAvailabilityStore } from '@/stores/booking-availability';
    import '@/assets/css/carousel.css';
    import { Carousel, Slide, Navigation } from 'vue3-carousel';

    const bookingStore = useBookingStore();
    const currentlySelectedTime = ref(bookingStore.state?.start_time);
    const dateCarousel = ref(null);
    const dates = ref(useAvailabilityStore().getAllData());
    const currentlySelectedDate = ref(null);

    // Computed Property for Transformed Time
    const dynamicTimes = computed(() => {
        // Ensure currentlySelectedDate is selected
        if (!currentlySelectedDate.value) {
            return [];
        }

        const selectedDateTimes = dates.value[currentlySelectedDate.value];

        // Check if the selected date has times and if it's an array
        if (!Array.isArray(selectedDateTimes)) {
            return [];
        }

        // Map over the times and transform them
        return selectedDateTimes.map((timeSlot) => {
            return {
                ...timeSlot,
                start_display: convertTo12Hour(timeSlot.start),
                end_display: convertTo12Hour(timeSlot.end),
            };
        });
    });

    // Function to calculate slides based on screen size
    const slidesByScreen = () => {
        if (window.innerWidth < 10) {
            return 2;
        } else if (window.innerWidth < 480) {
            return 3;
        } else if (window.innerWidth < 768) {
            return 3;
        } else if (window.innerWidth < 1024) {
            return 4;
        } else {
            return 5;
        }
    };

    // Initialize slidesByScreenValue as a reactive reference
    const slidesByScreenValue = ref(slidesByScreen());

    // Function to update slidesByScreenValue on window resize
    const updateSlidesByScreen = () => {
        slidesByScreenValue.value = slidesByScreen();
    };

    // Add the resize event listener on component mount and remove it on unmount
    onMounted(() => {
        window.addEventListener('resize', updateSlidesByScreen);

        if (bookingStore.state?.slide_index && dateCarousel.value) {
            dateCarousel.value.slideTo(bookingStore.state?.slide_index);
        }
        bookingStore.updateBookingState({
            experiment: 'combined-date-time',
        });

        // Set currentlySelectedDate to the first available date if not already set
        const firstDate = Object.keys(dates.value)[0];
        currentlySelectedDate.value = bookingStore.state?.selected_date
            ? bookingStore.state.selected_date
            : firstDate;

        // Also update the bookingStore if no selected_date is set
        if (!bookingStore.state?.selected_date) {
            bookingStore.updateBookingState({
                selected_date: firstDate,
                date: firstDate,
            });
        }
    });

    onUnmounted(() => {
        window.removeEventListener('resize', updateSlidesByScreen);
    });

    // Function to handle item selection
    const selectDate = (key, index) => {
        bookingStore.updateBookingState({
            selected_date: key,
            date: key,
            slide_index: index,
            start_time: '',
            end_time: '',
        });
        currentlySelectedDate.value = key;
        currentlySelectedTime.value = '';
    };

    const selectTime = (item) => {
        bookingStore.updateBookingState({
            start_time: item.start,
            end_time: item.end,
        });
        currentlySelectedTime.value = item.start;
    };
</script>

<!-- not scoped due to use using v-html for the subtitle -->
<style lang="scss" scoped>
    .show-limited {
        li:nth-child(1n + 8) {
            display: none;
        }
    }

    .high {
        color: var(--text-text-primary, #e40032);
    }

    .low {
        color: var(--text-text-success-strong, #00851d);
    }
</style>
